import React, { useState, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Manager, Reference, Popper } from 'react-popper';

import ClickOutside from 'shared/layout/ClickOutside';
import Checkbox from 'shared/elements/Checkbox';
import { getElementHeight, getVisibleHeight } from 'shared/utils/browser';

import {
  Dropdown,
  DropdownContainer,
  DropdownItem,
  DropdownTitle,
  DropdownTitleContainer,
  Error,
  HelperText,
  MultiDropdownDisplay
} from './styles';

function FormDropDownMulti({
  className,
  items = [],
  onChange,
  defaultSelected = null,
  error,
  fullSize = true,
  title,
  bgColor,
  disableSelect,
  getDisplayText,
  disable,
  inModal
}) {
  const [open, setOpen] = useState(false);
  const [maxHeight, setMaxHeight] = useState(320);
  const container = useRef(null);
  const [chosen, setChosen] = useState(defaultSelected || {});

  function click() {
    if (disable) return;
    setOpen(old => {
      if (inModal && old) {
        resetModalOverflow();
      }
      return !old;
    });
  }

  function onOutsideClick(e) {
    if (open) {
      e.preventDefault();
      e.stopPropagation();
      click();

      if (inModal) {
        resetModalOverflow();
      }
    }
  }

  function resetModalOverflow() {
    const mdl = document.getElementById('modal');
    mdl.parentNode.style.overflow = 'auto';
  }

  function select(event, d) {
    event.preventDefault();
    event.stopPropagation();

    if (disableSelect) return;
    setChosen(old => {
      const newChosen = {
        ...old,
        [d.value]: !old[d.value]
      };
      if (onChange) {
        onChange(newChosen);
      }
      return newChosen;
    });
  }

  function fixModalScroll() {
    if (!inModal) return;
    const mdl = document.getElementById('modal');
    const mdlHt = getElementHeight(mdl);
    const ddHt = getElementHeight(container.current);
    const viewHt = getVisibleHeight();
    if (mdlHt + ddHt >= viewHt - 50) {
      mdl.parentNode.style.overflow = 'auto';
    } else {
      mdl.parentNode.style.overflow = 'visible';
    }
  }

  function onClick() {
    if (disable) return;

    fixModalScroll();
    if (container.current) {
      const rect = container.current.getBoundingClientRect();
      const bottomDiff = window.innerHeight - rect.bottom;

      if (bottomDiff < 320) {
        const topDiff = rect.top;
        if (bottomDiff > topDiff) {
          setMaxHeight(bottomDiff - 10);
        } else if (topDiff < 320) {
          setMaxHeight(topDiff - 10);
        } else {
          setMaxHeight(320);
        }
      } else if (maxHeight !== 320) {
        setMaxHeight(320);
      }
    }
  }

  function getDisplay() {
    if (disableSelect) return '';
    let res = [];
    Object.keys(chosen).forEach(k => {
      if (chosen[k]) {
        items.forEach(i => {
          if (i.value === k) {
            res.push(i.display);
          }
        });
      }
    });
    return res.join(', ');
  }

  return (
    <ClickOutside onOutsideClick={onOutsideClick}>
      <div ref={container} onClick={onClick} style={{ position: 'relative' }} className={className}>
        <Manager>
          <Reference>
            {({ ref }) => (
              <DropdownTitleContainer
                ref={ref}
                error={error}
                onClick={click}
                bgColor={bgColor}
                fullSize={fullSize}
                disable={disable}
              >
                <DropdownTitle>
                  {(getDisplayText && getDisplayText(chosen)) || getDisplay() || title}
                </DropdownTitle>
              </DropdownTitleContainer>
            )}
          </Reference>
          {open && (
            <Popper placement="bottom-end" modifiers={popperModifiers}>
              {({ ref, style, placement, arrowProps }) => (
                <DropdownContainer
                  ref={ref}
                  style={style}
                  data-placement={placement}
                  fullSize={fullSize}
                >
                  <Dropdown open={open} maxHeight={maxHeight + 'px'}>
                    {items.map((item, i) => (
                      <DropdownItem
                        key={i}
                        danger={item.danger}
                        onClick={item.readOnly ? null : e => select(e, item)}
                        readOnly={item.readOnly}
                      >
                        <div>
                          <MultiDropdownDisplay>
                            <Checkbox checked={chosen[item.value] || false} />
                            {getDisplayText ? getDisplayText(item.display) : item.display}
                          </MultiDropdownDisplay>
                          {item.helpText && <HelperText>{item.helpText}</HelperText>}
                        </div>
                      </DropdownItem>
                    ))}
                  </Dropdown>
                  <div ref={arrowProps.ref} style={arrowProps.style} />
                </DropdownContainer>
              )}
            </Popper>
          )}
        </Manager>
        {error && <Error>{error}</Error>}
      </div>
    </ClickOutside>
  );
}

export default observer(FormDropDownMulti);

let popperModifiers = {
  preventOverflow: { enabled: false },
  hide: { enabled: false }
};
